import { createContext, useState, useContext, useEffect, type ReactNode } from 'react';
import jwtDecode from 'jwt-decode';
import { Amplify } from 'aws-amplify';
import {
  getCurrentUser,
  fetchAuthSession,
  signIn,
  signOut,
} from 'aws-amplify/auth';
import awsconfig from '@/aws-exports';
import { setupInterceptors } from '@/api/client';
import * as authApi from '@/api/auth';
import * as usersApi from '@/api/users';
import { idbClear } from '@/utils/idbCache';
import type { User } from '@/types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(Amplify.configure as (config: any) => void)(awsconfig);

const TOKEN_STORAGE_KEY = 'cognito_id_token';
const ACCESS_TOKEN_STORAGE_KEY = 'cognito_access_token';
const USER_STORAGE_KEY = 'current_user';

const safeLocalStorage = {
  get(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.error(`Failed to read ${key}:`, error);
      return null;
    }
  },
  set(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.error(`Failed to store ${key}:`, error);
    }
  },
  remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`Failed to remove ${key}:`, error);
    }
  },
};

const storeToken = (token: string) => safeLocalStorage.set(TOKEN_STORAGE_KEY, token);
const getStoredToken = (): string | null => safeLocalStorage.get(TOKEN_STORAGE_KEY);
const removeStoredToken = () => safeLocalStorage.remove(TOKEN_STORAGE_KEY);

const storeAccessToken = (token: string | undefined) => {
  if (token) safeLocalStorage.set(ACCESS_TOKEN_STORAGE_KEY, token);
};
const getStoredAccessToken = (): string | null => safeLocalStorage.get(ACCESS_TOKEN_STORAGE_KEY);
const removeStoredAccessToken = () => safeLocalStorage.remove(ACCESS_TOKEN_STORAGE_KEY);

const storeUser = (user: User) => safeLocalStorage.set(USER_STORAGE_KEY, JSON.stringify(user));
const getStoredUser = (): User | null => {
  const stored = safeLocalStorage.get(USER_STORAGE_KEY);
  if (!stored) return null;
  try {
    return JSON.parse(stored) as User;
  } catch (error) {
    console.error('Failed to parse stored user:', error);
    return null;
  }
};
const removeStoredUser = () => safeLocalStorage.remove(USER_STORAGE_KEY);

interface JwtPayload {
  exp: number;
  [key: string]: unknown;
}

const isTokenExpired = (token: string): boolean => {
  try {
    const decoded = jwtDecode<JwtPayload>(token);
    const currentTime = Date.now() / 1000;
    return decoded.exp < currentTime;
  } catch (error) {
    console.error('Failed to decode token:', error);
    return true;
  }
};

let _currentToken: string | null = null;

const configureInterceptors = (token: string | null): void => {
  _currentToken = token;
  setupInterceptors(
    () => _currentToken,
    () => {
      removeStoredToken();
      removeStoredAccessToken();
      removeStoredUser();
      if (window.location.pathname && !window.location.pathname.startsWith('/auth/')) {
        window.location.href = '/auth/login';
      }
    },
  );
};

export type RoleInput = string | string[];

export interface AuthResult {
  success: boolean;
  error?: string;
  message?: string;
  challenge?: 'NEW_PASSWORD_REQUIRED' | 'RESET_PASSWORD';
  email?: string;
  user?: User;
}

export interface AuthContextValue {
  currentUser: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<AuthResult>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<AuthResult>;
  updateProfile: (displayName: string) => Promise<AuthResult>;
  refreshCurrentUser: () => Promise<AuthResult>;
  forgotPassword: (email: string) => Promise<AuthResult>;
  logout: () => Promise<void>;
  hasRole: (role: RoleInput) => boolean;
  isAdmin: () => boolean;
  isStaff: () => boolean;
  isSuperAdmin: () => boolean;
  isFPAAdmin: () => boolean;
  isFPAUser: () => boolean;
  /** Capability check against resolved permissions, e.g. can('reporting','export'). */
  can: (resource: string, action: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = getStoredToken();
      const storedUser = getStoredUser();

      // Fast path: trust the cached user only if it carries the capability
      // list. A profile cached before `permissions` existed must fall through
      // to a backend refresh, otherwise can()-gated UI (the whole nav for
      // non-admins) renders empty. Once refreshed, the cached user includes
      // permissions and the fast path applies again (self-healing).
      if (
        storedToken &&
        storedUser &&
        Array.isArray(storedUser.permissions) &&
        !isTokenExpired(storedToken)
      ) {
        configureInterceptors(storedToken);
        setCurrentUser(storedUser);
        setIsAuthenticated(true);
        setIsLoading(false);
        return;
      } else if (storedToken) {
        removeStoredToken();
        removeStoredUser();
      }

      try {
        const cognitoUser = await getCurrentUser();
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        if (!idToken) throw new Error('No ID token');

        if (isTokenExpired(idToken)) {
          throw new Error('Token expired');
        }

        storeToken(idToken);
        configureInterceptors(idToken);

        try {
          const data = (await authApi.cognitoLogin(idToken)) as Record<string, unknown>;
          const userInfo: User = {
            id: data.id as User['id'],
            username:
              (data.display_name as string) ||
              (data.username as string) ||
              cognitoUser.username,
            display_name: data.display_name as string | undefined,
            email: data.email as string | undefined,
            role: data.role as string | undefined,
            roles: (data.roles as string[]) || [],
            permissions: (data.permissions as string[]) || [],
            tenant_id: (data.tenant_id as number | null | undefined) ?? null,
          };
          setCurrentUser(userInfo);
          setIsAuthenticated(true);
          storeUser(userInfo);
        } catch (backendError) {
          console.error('Backend login failed:', backendError);
          const userInfo: User = {
            id: cognitoUser.username,
            username: cognitoUser.username,
            role: 'public',
            roles: [],
          };
          setCurrentUser(userInfo);
          setIsAuthenticated(true);
          storeUser(userInfo);
        }
      } catch (_err) {
        removeStoredToken();
        removeStoredUser();
        configureInterceptors(null);
        setCurrentUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };
    initializeAuth();
  }, []);

  const _finaliseLogin = async (idToken: string, accessToken: string): Promise<void> => {
    storeToken(idToken);
    storeAccessToken(accessToken);
    configureInterceptors(idToken);
    // Member records are cached to IndexedDB and survive a session. Whoever
    // signs in next may hold fewer permissions than whoever cached them (a
    // demoted account, or a shared device), so start from an empty store
    // rather than trusting a blob fetched under someone else's grants.
    await idbClear('members');
    const data = (await authApi.cognitoLogin(idToken)) as Record<string, unknown>;
    const userInfo: User = {
      id: data.id as User['id'],
      username:
        (data.display_name as string) ||
        (data.username as string) ||
        (data.email as string) ||
        '',
      display_name: data.display_name as string | undefined,
      email: data.email as string | undefined,
      role: data.role as string | undefined,
      roles: (data.roles as string[]) || [],
      permissions: (data.permissions as string[]) || [],
      tenant_id: (data.tenant_id as number | null | undefined) ?? null,
    };
    setCurrentUser(userInfo);
    setIsAuthenticated(true);
    storeUser(userInfo);
  };

  const login = async (email: string, password: string): Promise<AuthResult> => {
    setError(null);
    setIsLoading(true);
    try {
      const { nextStep } = await signIn({ username: email, password });
      switch (nextStep.signInStep) {
        case 'DONE': {
          const session = await fetchAuthSession();
          await _finaliseLogin(
            session.tokens!.idToken!.toString(),
            session.tokens!.accessToken.toString(),
          );
          return { success: true };
        }
        case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
          return { success: false, challenge: 'NEW_PASSWORD_REQUIRED', email };
        case 'RESET_PASSWORD':
          return { success: false, challenge: 'RESET_PASSWORD', email };
        default:
          return {
            success: false,
            error: `Unsupported sign-in step: ${nextStep.signInStep}`,
          };
      }
    } catch (err) {
      const errorMessage = (err as Error)?.message || 'Login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const changePassword = async (
    currentPassword: string,
    newPassword: string,
  ): Promise<AuthResult> => {
    setError(null);
    const accessToken = getStoredAccessToken();
    if (!accessToken) {
      return { success: false, error: 'Not authenticated. Please sign in again.' };
    }
    try {
      const data = await authApi.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
        access_token: accessToken,
      });
      return { success: true, message: data.message };
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = err as any;
      const errorMessage = e?.response?.data?.detail || e?.message || 'Password change failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const updateProfile = async (displayName: string): Promise<AuthResult> => {
    setError(null);
    try {
      const data = (await usersApi.updateMe({ display_name: displayName })) as Record<
        string,
        unknown
      >;
      const updated: User = {
        ...(currentUser ?? { id: '', username: '', roles: [] }),
        username:
          (data.display_name as string) ||
          (data.username as string) ||
          currentUser?.username ||
          '',
        display_name: data.display_name as string | undefined,
        roles: (data.roles as string[]) || currentUser?.roles || [],
        permissions: (data.permissions as string[]) || currentUser?.permissions || [],
      };
      setCurrentUser(updated);
      storeUser(updated);
      return { success: true };
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = err as any;
      const errorMessage = e?.response?.data?.detail || e?.message || 'Profile update failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const refreshCurrentUser = async (): Promise<AuthResult> => {
    try {
      const data = (await usersApi.getMe()) as Record<string, unknown>;
      const refreshed: User = {
        ...(currentUser ?? { id: '', username: '', roles: [] }),
        id: data.id as User['id'],
        username:
          (data.display_name as string) ||
          (data.username as string) ||
          currentUser?.username ||
          '',
        display_name: data.display_name as string | undefined,
        email: data.email as string | undefined,
        role: data.role as string | undefined,
        roles: (data.roles as string[]) || [],
        permissions: (data.permissions as string[]) || [],
        tenant_id: (data.tenant_id as number | null | undefined) ?? null,
      };
      setCurrentUser(refreshed);
      storeUser(refreshed);
      return { success: true, user: refreshed };
    } catch (err) {
      console.error('refreshCurrentUser failed:', err);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = err as any;
      return { success: false, error: e?.response?.data?.detail || e?.message };
    }
  };

  const forgotPassword = async (email: string): Promise<AuthResult> => {
    setError(null);
    setIsLoading(true);
    try {
      const data = await authApi.forgotPassword(email);
      return { success: true, message: data.message };
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = err as any;
      const errorMessage =
        e?.response?.data?.detail || e?.message || 'Failed to send password reset link';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken?.toString();
      if (accessToken) {
        await authApi.signout(accessToken);
      }
    } catch (err) {
      console.error('Backend signout error:', err);
    }
    try {
      await signOut();
    } catch (err) {
      console.error('Cognito local signout error:', err);
    }
    removeStoredToken();
    removeStoredAccessToken();
    removeStoredUser();
    await idbClear('members');
    configureInterceptors(null);
    setCurrentUser(null);
    setIsAuthenticated(false);
  };

  const hasRole = (role: RoleInput): boolean => {
    if (!currentUser) return false;
    const owned = [
      ...(currentUser.roles || []).map((r) => String(r).toUpperCase()),
      ...(currentUser.role ? [String(currentUser.role).toUpperCase()] : []),
    ];
    if (Array.isArray(role)) {
      return role.some((r) => owned.includes(String(r).toUpperCase()));
    }
    return owned.includes(String(role).toUpperCase());
  };

  const isAdmin = () => hasRole('admin');
  const isStaff = () => hasRole(['admin', 'staff', 'super_admin', 'fpa_admin']);
  const isSuperAdmin = () => hasRole('super_admin');
  const isFPAAdmin = () => hasRole('fpa_admin');
  const isFPAUser = () => hasRole('fpa_user');

  // Capability check against the resolved permission names from the backend.
  // Permission names are `${resource}_${action}`; owner-scoped variants carry
  // a `_own` suffix and still grant the button (the server enforces ownership).
  // Super admins carry the wildcard '*'.
  const can = (resource: string, action: string): boolean => {
    const perms = currentUser?.permissions || [];
    if (perms.includes('*')) return true;
    const base = `${resource}_${action}`;
    return perms.includes(base) || perms.includes(`${base}_own`);
  };

  const value: AuthContextValue = {
    currentUser,
    isAuthenticated,
    isLoading,
    error,
    login,
    changePassword,
    updateProfile,
    refreshCurrentUser,
    forgotPassword,
    logout,
    hasRole,
    isAdmin,
    isStaff,
    isSuperAdmin,
    isFPAAdmin,
    isFPAUser,
    can,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
